/**
 * Service 3D pour Module Soudures
 * 
 * Gestion de la géométrie Three.js pour l'affichage des soudures
 * Performance optimisée selon cahier des charges
 */

import * as THREE from 'three';
import {
  StructureSoudures,
  Soudure,
  TypeSoudure,
  Point3D
} from '../types';

/**
 * Service de génération 3D pour les soudures
 * Isolation complète - aucun impact sur autres modules
 */
export class Soudures3DService {
  private scene: THREE.Scene;
  private group: THREE.Group;
  private materials: { [key: string]: THREE.Material };
  private geometriePartagee: THREE.SphereGeometry;
  private instancedMeshes: Map<string, THREE.InstancedMesh>;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.group = new THREE.Group();
    this.group.name = 'soudures';
    
    // Géométrie partagée ULTRA-OPTIMISÉE pour les soudures
    this.geometriePartagee = new THREE.SphereGeometry(
      0.02, // Taille par défaut
      4,    // Segments horizontaux TRÈS réduits pour performance
      3     // Segments verticaux TRÈS réduits pour performance
    );
    
    // Map pour les meshes instanciés par type
    this.instancedMeshes = new Map();
    
    // Matériaux ULTRA-OPTIMISÉS pour les soudures (performance maximale)
    this.materials = {
      default: new THREE.MeshBasicMaterial({ color: 0xFF0000 }), // Rouge par défaut
      montantPoutre: new THREE.MeshBasicMaterial({ color: 0xFF0000 }),
      traverseMontant: new THREE.MeshBasicMaterial({ color: 0xFF4444 }),
      poutrePoutre: new THREE.MeshBasicMaterial({ color: 0xFF6666 }),
      traverseTraverse: new THREE.MeshBasicMaterial({ color: 0xFF8888 }),
      accessoire: new THREE.MeshBasicMaterial({ color: 0xFFAAAA })
    };
  }

  /**
   * Génère la géométrie 3D complète des soudures avec optimisation par instancing
   */
  public genererSoudures3D(structureSoudures: StructureSoudures): THREE.Group {
    // Nettoyage du groupe précédent
    this.nettoyerGroupe();

    if (!structureSoudures || !structureSoudures.soudures.length) {

      return this.group;
    }

    try {
      // Mettre à jour la couleur globale
      this.mettreAJourCouleur(structureSoudures.parametres.couleur);

      // Grouper les soudures par type pour l'instancing
      const souduresParType = new Map<TypeSoudure, Soudure[]>();
      
      structureSoudures.soudures.forEach(soudure => {
        if (soudure.visible) {
          if (!souduresParType.has(soudure.type)) {
            souduresParType.set(soudure.type, []);
          }
          souduresParType.get(soudure.type)!.push(soudure);
        }
      });

      // Créer un mesh instancié pour chaque type de soudure
      souduresParType.forEach((soudures, type) => {
        const instancedMesh = this.creerMeshInstancie(type, soudures);
        if (instancedMesh) {
          this.group.add(instancedMesh);
          this.instancedMeshes.set(type, instancedMesh);
        }
      });



    } catch (error) {
      console.error('[Soudures3D] Erreur génération:', error);
    }

    return this.group;
  }

  /**
   * Crée un mesh instancié pour un type de soudure (optimisation performance)
   */
  private creerMeshInstancie(type: TypeSoudure, soudures: Soudure[]): THREE.InstancedMesh | null {
    try {
      if (soudures.length === 0) return null;

      // Matériau selon le type de soudure
      const material = this.obtenirMateriauParType(type);

      // Création du mesh instancié
      const instancedMesh = new THREE.InstancedMesh(
        this.geometriePartagee,
        material,
        soudures.length
      );

      // Matrice temporaire pour les transformations
      const matrix = new THREE.Matrix4();
      const scale = new THREE.Vector3();

      // Configurer chaque instance
      soudures.forEach((soudure, index) => {
        // Calculer l'échelle basée sur la taille de la soudure
        const facteurEchelle = soudure.taille / 0.02; // 0.02 est la taille par défaut
        scale.set(facteurEchelle, facteurEchelle, facteurEchelle);

        // Créer la matrice de transformation
        matrix.compose(
          new THREE.Vector3(soudure.position.x, soudure.position.y, soudure.position.z),
          new THREE.Quaternion(), // Pas de rotation
          scale
        );

        // Appliquer la matrice à l'instance
        instancedMesh.setMatrixAt(index, matrix);
      });

      // Mettre à jour les matrices d'instances
      instancedMesh.instanceMatrix.needsUpdate = true;

      // Métadonnées
      instancedMesh.name = `soudures_${type}`;
      instancedMesh.userData = {
        type: 'soudures_instanciees',
        soudureType: type,
        count: soudures.length
      };

      // Optimisation performance : désactiver ombres et frustum culling
      instancedMesh.castShadow = false;
      instancedMesh.receiveShadow = false;
      instancedMesh.frustumCulled = false;

      return instancedMesh;

    } catch (error) {
      console.error(`[Soudures3D] Erreur création mesh instancié ${type}:`, error);
      return null;
    }
  }

  /**
   * Obtient le matériau approprié selon le type de soudure
   */
  private obtenirMateriauParType(type: TypeSoudure): THREE.Material {
    switch (type) {
      case TypeSoudure.MONTANT_POUTRE:
        return this.materials.montantPoutre;
      case TypeSoudure.TRAVERSE_MONTANT:
        return this.materials.traverseMontant;
      case TypeSoudure.POUTRE_POUTRE:
        return this.materials.poutrePoutre;
      case TypeSoudure.TRAVERSE_TRAVERSE:
        return this.materials.traverseTraverse;
      case TypeSoudure.ACCESSOIRE:
        return this.materials.accessoire;
      default:
        return this.materials.default;
    }
  }

  /**
   * Met à jour la couleur de tous les matériaux
   */
  private mettreAJourCouleur(couleur: string): void {
    const color = new THREE.Color(couleur);
    
    // Appliquer la couleur de base à tous les matériaux avec des variations
    (this.materials.default as THREE.MeshLambertMaterial).color = color;
    (this.materials.montantPoutre as THREE.MeshLambertMaterial).color = color;
    
    // Variations légères pour différencier les types
    const colorVariant1 = color.clone().multiplyScalar(0.9);
    const colorVariant2 = color.clone().multiplyScalar(0.8);
    const colorVariant3 = color.clone().multiplyScalar(0.7);
    const colorVariant4 = color.clone().multiplyScalar(0.6);
    
    (this.materials.traverseMontant as THREE.MeshLambertMaterial).color = colorVariant1;
    (this.materials.poutrePoutre as THREE.MeshLambertMaterial).color = colorVariant2;
    (this.materials.traverseTraverse as THREE.MeshLambertMaterial).color = colorVariant3;
    (this.materials.accessoire as THREE.MeshLambertMaterial).color = colorVariant4;
  }

  /**
   * Nettoie le groupe des soudures et les meshes instanciés
   */
  private nettoyerGroupe(): void {
    // Nettoyer les meshes instanciés
    this.instancedMeshes.clear();
    
    while (this.group.children.length > 0) {
      const child = this.group.children[0];
      this.group.remove(child);
      
      // Pas besoin de disposer la géométrie car elle est partagée
      // Seuls les meshes instanciés sont supprimés
    }
  }

  /**
   * Libère toutes les ressources
   */
  public dispose(): void {
    this.nettoyerGroupe();
    
    // Libérer la géométrie partagée
    if (this.geometriePartagee) {
      this.geometriePartagee.dispose();
    }
    
    // Libérer les matériaux
    Object.values(this.materials).forEach(material => {
      if (material instanceof THREE.Material) {
        material.dispose();
      }
    });
    
    // Retirer le groupe de la scène
    if (this.scene && this.group.parent) {
      this.scene.remove(this.group);
    }
  }

  /**
   * Obtient le groupe 3D
   */
  public getGroup(): THREE.Group {
    return this.group;
  }

  /**
   * Met à jour la visibilité par type de soudure (optimisé pour meshes instanciés)
   */
  public mettreAJourVisibiliteParType(type: TypeSoudure, visible: boolean): void {
    const instancedMesh = this.instancedMeshes.get(type);
    if (instancedMesh) {
      instancedMesh.visible = visible;
    }
  }

  /**
   * Met à jour la visibilité globale de toutes les soudures
   */
  public mettreAJourVisibiliteGlobale(visible: boolean): void {
    this.group.visible = visible;
  }
}

/**
 * Utilitaires de conversion pour les soudures
 */
export class SouduresGeometryUtils {
  /**
   * Conversion Point3D vers Vector3
   */
  static pointToVector3(point: Point3D): THREE.Vector3 {
    return new THREE.Vector3(point.x, point.y, point.z);
  }

  /**
   * Conversion Vector3 vers Point3D
   */
  static vector3ToPoint(vector: THREE.Vector3): Point3D {
    return { x: vector.x, y: vector.y, z: vector.z };
  }

  /**
   * Calcule la distance entre deux points de soudure
   */
  static calculerDistance(p1: Point3D, p2: Point3D): number {
    return Math.sqrt(
      Math.pow(p1.x - p2.x, 2) +
      Math.pow(p1.y - p2.y, 2) +
      Math.pow(p1.z - p2.z, 2)
    );
  }
}